import { useState, useEffect } from 'react';
import axios from 'axios';
import { Smartphone, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';
import { APP_SCREENS as SCREENS, PhoneFrame } from '@/components/AppShellPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileAppShellPanel = () => {
  const { currentProject, brandKit } = useProject();

  const [content, setContent] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [screenId, setScreenId] = useState('home');

  const fetchContent = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/site-content`);
      setContent(res.data);
    } catch (error) {
      console.error('Failed to fetch site content:', error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [currentProject?.id]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/site-content/generate`);
      setContent(res.data);
      toast.success('App content generated');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate app content');
    } finally {
      setGenerating(false);
    }
  };

  if (!currentProject) return null;

  const primary = brandKit?.colors?.primary?.hex || '#1B3A5C';

  return (
    <div className="h-full overflow-auto p-6" data-testid="mobile-app-shell-panel">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Mobile App Shell</h2>
              <p className="text-muted-foreground">Clickable, brand-themed prototype screens &mdash; the real app build happens after sign-off</p>
            </div>
            <ReportButton section="website" projectId={currentProject.id} />
          </div>

          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
                    Content
                    <ProviderBadge provider={content?.generated_by} />
                  </h3>
                  <p className="text-sm text-muted-foreground">Shares the same copy as the Website Shell</p>
                </div>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="app-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating copy &amp; imagery... (can take a few minutes)</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />{content ? 'Regenerate Content' : 'Generate Content'}</>
                )}
              </Button>
            </div>
          </div>

          {fetching && !content ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : !content ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Smartphone className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No app content yet. Generate some to see the screens.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* Screen switcher */}
              <div className="flex items-center gap-2 mb-6">
                {SCREENS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setScreenId(s.id)}
                    className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                      screenId === s.id
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                    }`}
                    data-testid={`app-screen-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Phone frame */}
              <PhoneFrame
                screenId={screenId}
                onScreenChange={setScreenId}
                brandKit={brandKit}
                content={content}
                primaryColor={primary}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAppShellPanel;
